/**
 * PERSONALENS DATALAGER — det Personal.tsx läser och skriver genom.
 *
 * =========================================================================
 * LISTAN KOMMER UR SYNC_PULL, ÄNDRINGARNA UR TRE EGNA RUTTER.
 * Det är inte en inkonsekvens utan kontraktets uppdelning: firmans DO bär
 * member-raden (namn, e-post, roll, org.nr, ägarflagga) och den går ut i
 * SYNC_PULL som en tabell bland femton, medan MEDLEMSKAPET ligger i den delade
 * D1-databasen och ändras med ADD_MEMBER, SET_MEMBER_ROLE och REMOVE_MEMBER.
 * Servern skriver båda i samma anrop (worker/src/modules/company/members.ts).
 * Klienten läser alltså på ett ställe och skriver på ett annat, och därför görs
 * en NY PULL efter varje lyckad ändring i stället för att raden lappas ihop
 * lokalt ur svaret.
 * =========================================================================
 *
 * SVARET PÅ EN ÄNDRING ÄR ETT KVITTO, INTE EN RAD ATT VISA. MemberMutationResponse
 * är MembershipRow — D1-raden — och den bär varken namn eller ägarflagga. Den
 * duger som bekräftelse på att skrivningen gick igenom; listan hämtas om.
 *
 * OP_ID ÄR ANROPARENS (beslut 09). Alla tre rutterna bär idempotency 'op_id':
 * mynta ETT när användaren trycker, spara det med kroppen och skicka SAMMA vid
 * varje omsändning — annars blir ett omtag efter ett tappat nät en andra
 * inbjudan i stället för ett uppspelat svar.
 *
 * PRISET RÄKNAS ALDRIG FÖR HAND. `kostarPengar` läser PAID_ROLES och
 * `prisbesked` läser PRICE_PER_USER_ORE, båda ur kontraktet. Talet 119 står inte
 * i den här filen och ska inte skrivas in i den.
 */

import { useEffect, useState } from 'react';
import { ADD_MEMBER, ALL_ROLES, REMOVE_MEMBER, SET_MEMBER_ROLE, SYNC_PULL } from '@delat/routes';
import {
  PAID_ROLES,
  PRICE_PER_USER_ORE,
  SYNC_PULL_MAX_LIMIT,
  asSeq,
} from '@delat/types';
import type {
  AccountId,
  AddMemberRequest,
  CompanyId,
  MemberMutationResponse,
  MemberRow,
  OpId,
  OrgNumber,
  RemoveMemberResponse,
  Role,
  SetMemberRoleRequest,
} from '@delat/types';
import { apifel, granska, klienten, lyckades, misslyckades, nyUlid } from '../api';
import type { Klartidfel, Resultat } from '../api';
import {
  granskaBorttagen,
  granskaMedlemskap,
  granskaSyncPull,
} from '../api/granskare/personal';
import { visaOren } from '../app/datum';
import { ROLLNAMN } from './oversikt';

/* == Rollerna, och vad de kostar ========================================= */

export interface Rollval {
  readonly varde: Role;
  readonly etikett: string;
}

/** "chef" -> "Chef". ROLLNAMN är gemener; en väljare börjar med versal. */
export const rollrubrik = (roll: Role): string => {
  const namn = ROLLNAMN[roll];
  return `${namn.charAt(0).toUpperCase()}${namn.slice(1)}`;
};

/** Kontraktets egen ordning (ALL_ROLES), aldrig en handskriven lista. */
export const ROLLVALEN: readonly Rollval[] = ALL_ROLES.map((roll) => ({
  varde: roll,
  etikett: rollrubrik(roll),
}));

/**
 * BESLUT 18: en person kostar genom att vara UPPLAGD med en betald roll, oavsett
 * om han rapporterat något. Mängden är PAID_ROLES och inte PriceRole — den
 * inhyrde HAR ett timpris mot kundens kund men kostar ingenting hos Klartid.
 */
export const kostarPengar = (roll: Role): boolean =>
  PAID_ROLES.some((betald) => betald === roll);

/** Texten bredvid rollväljaren. Talet läses ur kontraktet, aldrig skrivet. */
export const prisbesked = (roll: Role): string =>
  kostarPengar(roll)
    ? `Kostar ${visaOren(PRICE_PER_USER_ORE)} per månad ex moms från nästa dragning`
    : 'Kostar ingenting';

/** '5593026494' -> '559302-6494'. Så skriver man ett org.nr för en människa. */
export const visaOrgnummer = (nummer: string): string =>
  nummer.length === 10 ? `${nummer.slice(0, 6)}-${nummer.slice(6)}` : nummer;

/* == Fälten användaren fyller i ==========================================
   Klientens förkontroller är BARA till för att slippa en resa för ett uppenbart
   fel. Servern gör den riktiga (parseAddMember mot schemats CHECK) och svarar
   VALIDATION_FAILED { field }, som `faltFor` nedan lägger på rätt fält.
   ======================================================================== */

export const rensaEpost = (skrivet: string): string => skrivet.trim().toLowerCase();

/**
 * Speglar worker/src/http/validate.ts requireEmail, utom längdtaket: ett '@'
 * som varken står först eller sist, inga blanksteg, och en punkt efter
 * snabel-a. Taket (254 tecken) är ett tal i serverkoden och skrivs med flit
 * INTE av här — se LUCKOR i överlämningen; en för lång adress får serverns egen
 * VALIDATION_FAILED { field: 'email' } och hamnar på fältet ändå.
 */
export const arEpost = (epost: string): boolean => {
  if (epost === '' || epost.includes(' ')) return false;
  const snabel = epost.indexOf('@');
  if (snabel < 1 || snabel === epost.length - 1) return false;
  return epost.slice(snabel + 1).includes('.');
};

/** Tio siffror. Bindestreck är det användaren skriver, aldrig det som lagras. */
export const rensaOrgnummer = (skrivet: string): string => skrivet.replace(/[\s-]/g, '');

export const arOrgnummer = (siffror: string): boolean => /^\d{10}$/.test(siffror);

/** Märker numret genom kontraktets egen konstruktor. */
export const tillOrgnummer = (siffror: string, falt: string): OrgNumber =>
  granska.orgnummer(siffror, falt);

export interface Faltbrist {
  readonly falt: string;
  readonly text: string;
}

/**
 * VAR ETT SERVERFEL SKA SYNAS. Texten är ALLTID kontraktets egen (api/fel.ts
 * hämtar den ur shared/errors.ts) — den skrivs aldrig om här.
 *
 * DE TVÅ GÄSTFELEN (beslut 17) kommer från olika ställen i servern men handlar
 * om SAMMA fält: GUEST_ORG_NUMBER_MISSING när numret saknas helt,
 * GUEST_ORG_NUMBER_INVALID när det pekar på firman själv eller på en firma
 * personen äger. Båda hör hemma på org.nr-fältet.
 *
 * ROW_DUPLICATE på ADD_MEMBER betyder att personen redan är medlem, och det är
 * E-POSTEN som identifierar honom — därför landar det där och inte i en ruta
 * över formuläret.
 */
export const faltFor = (fel: Klartidfel): Faltbrist | null => {
  if (fel.slag !== 'api') return null;
  if (fel.code === 'GUEST_ORG_NUMBER_MISSING' || fel.code === 'GUEST_ORG_NUMBER_INVALID') {
    return { falt: 'guest_org_number', text: fel.text };
  }
  if (fel.code === 'ROW_DUPLICATE') return { falt: 'email', text: fel.text };
  if (fel.code === 'VALIDATION_FAILED') {
    const falt = fel.detalj?.['field'];
    if (
      falt === 'email' ||
      falt === 'name' ||
      falt === 'role' ||
      falt === 'guest_org_number'
    ) {
      return { falt, text: fel.text };
    }
  }
  return null;
};

/* == Listan ==============================================================
   Ingen listrutt finns: SYNC_PUSH och SYNC_PULL är den ENDA kanalen mot
   företagsdata (shared/routes.ts). Steg 5 äger den lokala databasen; tills dess
   hämtas hela firman från since = 0 vid varje besök. Det är medvetet enkelt och
   medvetet tillfälligt — allt ligger bakom `usePersonallager`.
   ======================================================================== */

export interface Personallager {
  /** Levande medlemmar, ägaren först och sedan i rollordning och namnordning. */
  readonly medlemmar: readonly MemberRow[];
  /** Antal med en betald roll — exakt den fråga faktureringen ställer. */
  readonly betalda: number;
}

export type Personallage =
  | { readonly status: 'laddar' }
  | { readonly status: 'fel'; readonly fel: Klartidfel }
  | { readonly status: 'klar'; readonly lager: Personallager };

/**
 * Taket är en spärr mot en server som svarar has_more i evighet, inte en
 * datagräns: SYNC_PULL_MAX_LIMIT rader per varv gånger taket räcker långt förbi
 * en firma med fem man.
 */
const MAX_VARV = 200;

const ROLLORDNING: Readonly<Record<Role, number>> = {
  manager: 0,
  worker: 1,
  apprentice: 2,
  guest: 3,
  bookkeeper: 4,
};

/** Ägaren först — han är den enda raden som inte går att röra (beslut 14). */
const sortera = (medlemmar: readonly MemberRow[]): readonly MemberRow[] =>
  medlemmar.slice().sort((a, b) => {
    if (a.is_owner !== b.is_owner) return b.is_owner - a.is_owner;
    const roller = ROLLORDNING[a.role] - ROLLORDNING[b.role];
    return roller !== 0 ? roller : a.name.localeCompare(b.name, 'sv');
  });

export const hamtaMedlemmar = async (
  firmaId: CompanyId,
  signal: AbortSignal,
): Promise<Resultat<Personallager>> => {
  const medlemmar = new Map<string, MemberRow>();
  let since = asSeq(0);

  for (let varv = 0; varv < MAX_VARV; varv += 1) {
    const svar = await klienten.anrop(
      SYNC_PULL,
      { params: { company_id: firmaId }, data: { since, limit: SYNC_PULL_MAX_LIMIT }, signal },
      granskaSyncPull,
    );
    if (!svar.ok) return misslyckades(svar.fel);

    const pull = svar.varde;
    if (pull.view !== 'full') {
      // Rollen läses ur D1 vid VARJE anrop, så det här är en degraderad chef:
      // beviset dog inte, men han får inte längre se laget. Montörsvyns
      // member-rader saknar dessutom e-post, org.nr och ägarflagga (beslut 16),
      // och utan dem finns ingen personalskärm att visa.
      return misslyckades(apifel('FORBIDDEN', undefined, ''));
    }

    // Högsta seq vinner: en pull i flera vändor kan få samma rad två gånger om
    // någon skriver mitt i (schema/company.sql ger varje skrivning ett nytt seq).
    for (const rad of pull.changes.member) {
      const gammal = medlemmar.get(rad.account_id);
      if (gammal === undefined || rad.seq > gammal.seq) medlemmar.set(rad.account_id, rad);
    }

    if (!pull.has_more) {
      // MJUKRADERADE KOMMER MED I PULLEN just för att kunna tas bort här. En
      // borttagen medlem ligger kvar i firmans DO så att gamla tidrader kan visa
      // vems tid det var — men han är inte upplagd och kostar ingenting.
      const levande = sortera([...medlemmar.values()].filter((m) => m.deleted_at === null));
      return lyckades({
        medlemmar: levande,
        betalda: levande.filter((m) => kostarPengar(m.role)).length,
      });
    }
    if (Number(pull.next_since) <= Number(since)) {
      // Markören står still trots has_more. Ett varv till hade snurrat i evighet
      // på samma fönster.
      return misslyckades(apifel('SERVER_ERROR', undefined, ''));
    }
    since = pull.next_since;
  }
  return misslyckades(apifel('SERVER_ERROR', undefined, ''));
};

/** Hela listan, hämtad om när `omgang` bumpas — efter varje lyckad ändring. */
export const usePersonallager = (firmaId: CompanyId, omgang: number): Personallage => {
  const [lage, sattLage] = useState<Personallage>({ status: 'laddar' });

  useEffect(() => {
    const styrning = new AbortController();
    let aktuell = true;
    sattLage({ status: 'laddar' });

    void (async () => {
      const svar = await hamtaMedlemmar(firmaId, styrning.signal);
      if (!aktuell) return;
      // Ett avbrutet anrop är inte ett fel — skärmen lämnades.
      if (!svar.ok && svar.fel.slag === 'nat' && svar.fel.avbrutet) return;
      sattLage(svar.ok ? { status: 'klar', lager: svar.varde } : { status: 'fel', fel: svar.fel });
    })();

    return () => {
      aktuell = false;
      styrning.abort();
    };
  }, [firmaId, omgang]);

  return lage;
};

/* == Skrivningarna ======================================================= */

export const nyttOpId = (): OpId => granska.opId(nyUlid(), 'op_id');

/**
 * ADD_MEMBER SKICKAR INGET MEJL. Servern skapar kontoraden och medlemskapet och
 * ingenting mer (worker/src/modules/company/members.ts) — personen måste själv
 * öppna appen och skriva sin mejladress för att få en inloggningskod.
 * Skärmen SKA säga det efter varje lyckad inbjudan; annars kommer han aldrig in.
 */
export const bjudIn = (
  firmaId: CompanyId,
  opId: OpId,
  begaran: AddMemberRequest,
): Promise<Resultat<MemberMutationResponse>> =>
  klienten.anrop(
    ADD_MEMBER,
    { params: { company_id: firmaId }, data: begaran, op_id: opId },
    granskaMedlemskap,
  );

export const bytRoll = (
  firmaId: CompanyId,
  kontoId: AccountId,
  opId: OpId,
  begaran: SetMemberRoleRequest,
): Promise<Resultat<MemberMutationResponse>> =>
  klienten.anrop(
    SET_MEMBER_ROLE,
    { params: { company_id: firmaId, account_id: kontoId }, data: begaran, op_id: opId },
    granskaMedlemskap,
  );

/**
 * REMOVE_MEMBER raderar medlemskapet HÅRT i D1 och mjukraderar member-raden i
 * firmans DO. Personens gamla tidrader står kvar med hans namn — och han slutar
 * kosta pengar, vilket är hela skälet att raden finns.
 */
export const taBort = (
  firmaId: CompanyId,
  kontoId: AccountId,
  opId: OpId,
): Promise<Resultat<RemoveMemberResponse>> =>
  klienten.anrop(
    REMOVE_MEMBER,
    { params: { company_id: firmaId, account_id: kontoId }, op_id: opId },
    granskaBorttagen,
  );
